/**
 * Vincent B849 (Eric 2026-02-14 SPEC STRICK PROD) — Sous-onglet
 * « Mes favoris » sur la fiche vidéo.
 *
 * Regroupe TOUS les items favorisés par l'utilisateur courant sur
 * cette vidéo, à travers les 5 panneaux de connaissances (Concepts,
 * Q&R, Procédures, Scripts, Objections). Clic sur une carte → ouvre
 * le `KnowledgeDetailModal` partagé (parité avec les autres panneaux).
 *
 * Vide ? Message explicite qui pointe vers le ♥ de chaque carte.
 *
 * Note : les scripts (collection `sales_scripts`) sont inclus via la
 * prop `scriptsList` — leur item_id est ajouté au Set `favKuIds` par
 * `KUFavoriteHeart` (même mécanisme unifié).
 */
import React from "react";
import { Heart } from "@phosphor-icons/react";
import { TYPE_COLORS } from "../../lib/videoDetailFormat";

const TYPE_LABELS = {
  concept: { fr: "Concept", en: "Concept" },
  qa: { fr: "Q&R", en: "Q&R" },
  procedure: { fr: "Procédure", en: "Procedure" },
  script: { fr: "Script", en: "Script" },
  sales_script: { fr: "Script", en: "Script" },
  objection: { fr: "Objection", en: "Objection" },
  rebuttal: { fr: "Réfutation", en: "Rebuttal" },
  synthesis: { fr: "Synthèse", en: "Synthesis" },
};

const _typeKey = (t) => {
  if (t === "sales_script" || t === "sales-script") return "script";
  if (t === "rebuttal") return "objection";
  if (t === "process") return "procedure";
  return t || "concept";
};

const _colorFor = (type) => {
  const k = _typeKey(type);
  return TYPE_COLORS[k]?.color || TYPE_COLORS.concept.color;
};

export default function FavoritesTabPanel({
  show,
  lang,
  knowledgeUnits,
  scriptsList,
  favKuIds,
  setDetailModalKu,
}) {
  if (!show) return null;

  const kuFavorites = (knowledgeUnits || []).filter((u) => favKuIds.has(u.id));
  const scriptFavorites = (scriptsList || []).filter((s) => favKuIds.has(s.id));

  // On tag les scripts avec un pseudo-type pour uniformiser l'affichage
  const scriptFavsTyped = scriptFavorites.map((s) => ({ ...s, type: "script" }));
  const all = [...kuFavorites, ...scriptFavsTyped];

  // Groupement par type pour un rendu organisé
  const byType = {};
  for (const item of all) {
    const k = _typeKey(item.type);
    if (!byType[k]) byType[k] = [];
    byType[k].push(item);
  }
  const ordered = ["concept", "qa", "procedure", "script", "objection", "synthesis"];
  const groups = ordered.filter((k) => byType[k]?.length > 0);

  if (all.length === 0) {
    return (
      <div className="p-8 text-center" data-testid="favorites-panel-empty">
        <Heart size={48} weight="regular" className="mx-auto mb-3 text-rose-300" />
        <p className="text-sm font-semibold text-zinc-700 mb-1">
          {lang === "fr" ? "Aucun favori pour cette vidéo." : "No favorite for this video yet."}
        </p>
        <p className="text-xs text-zinc-500 max-w-md mx-auto leading-snug">
          {lang === "fr"
            ? "Clique sur le ♥ à côté d'un concept, d'une Q&R, d'une procédure, d'un script ou d'une objection pour l'ajouter ici. Tes favoris te suivent d'une session à l'autre."
            : "Click the ♥ next to any concept, Q&R, procedure, script or objection to add it here. Your favorites persist across sessions."}
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4" data-testid="favorites-panel">
      <div className="flex items-center justify-between border-b border-zinc-200 pb-2">
        <div className="flex items-center gap-2">
          <Heart size={18} weight="fill" className="text-rose-500" />
          <h3 className="text-sm font-bold text-zinc-800">
            {lang === "fr"
              ? `${all.length} favori${all.length > 1 ? "s" : ""} sur cette vidéo`
              : `${all.length} favorite${all.length > 1 ? "s" : ""} on this video`}
          </h3>
        </div>
        <div className="text-[10px] text-zinc-500 tabular-nums" data-testid="favorites-panel-summary">
          {groups.map((k, i) => (
            <span key={k}>
              {i > 0 && <span className="mx-1 text-zinc-300">·</span>}
              <span style={{ color: _colorFor(k) }} className="font-semibold">
                {byType[k].length}
              </span>
              <span className="ml-1">{TYPE_LABELS[k]?.[lang] || k}</span>
            </span>
          ))}
        </div>
      </div>

      {groups.map((groupKey) => (
        <div key={groupKey} className="space-y-1.5" data-testid={`favorites-group-${groupKey}`}>
          <div
            className="text-[10px] uppercase tracking-wider font-bold"
            style={{ color: _colorFor(groupKey) }}
          >
            {TYPE_LABELS[groupKey]?.[lang] || groupKey}
            <span className="ml-2 text-zinc-400 tabular-nums">({byType[groupKey].length})</span>
          </div>
          {byType[groupKey].map((item) => {
            const title = item.title || item.content?.title || item.name || item.scenario || "(sans titre)";
            const ts = typeof item.timestamp === "number"
              ? item.timestamp
              : (item.timestamps?.start ?? null);
            const mmss = ts != null
              ? `${String(Math.floor(ts / 60)).padStart(2, "0")}:${String(Math.floor(ts % 60)).padStart(2, "0")}`
              : null;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setDetailModalKu?.(item)}
                className="w-full flex items-center gap-2 p-2 rounded border border-border hover:bg-secondary/30 transition-colors text-left cursor-pointer hover:border-rose-300"
                data-testid={`favorite-card-${item.id}`}
              >
                <Heart size={14} weight="fill" className="text-rose-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-zinc-800 truncate">{title}</div>
                  {mmss && (
                    <div className="text-[10px] text-zinc-500 font-mono tabular-nums">{mmss}</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
